// src/services/configuracionService.ts
import { api, getAccessToken } from './api';
import type {
  Perfil,
  PerfilUpdate,
  CambiarPassword,
  UsuarioAdmin,
  UsuarioCreate,
  UsuarioUpdate,
  RolAdmin,
  RolCreate,
  TipoTicketConfig,
  TipoActividadConfig,
  CategoriaConocimientoConfig,
  ModuloConfig,
  ModuloCreate,
  ModuloUpdate,
} from '@/types';

export const configuracionService = {
  // Perfil
  obtenerPerfil: () =>
    api.get<Perfil>('/auth/me'),

  actualizarPerfil: (data: PerfilUpdate) =>
    api.patch<Perfil>('/auth/me', data),

  cambiarPassword: (data: CambiarPassword) =>
    api.put('/auth/me/password', data),

  cambiarAvatar: async (file: File): Promise<{ avatar_url: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
    const token = getAccessToken();

    const response = await fetch(`${API_BASE_URL}/auth/me/avatar`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Error al subir avatar');
    }

    return response.json();
  },

  // Usuarios (ADMIN)
  listarUsuarios: (params?: { tipo?: string; activo?: boolean; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.tipo) searchParams.set('tipo', params.tipo);
    if (params?.activo !== undefined) searchParams.set('activo', String(params.activo));
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return api.get<UsuarioAdmin[]>(`/usuarios${query ? `?${query}` : ''}`);
  },

  obtenerUsuario: (id: string) =>
    api.get<UsuarioAdmin>(`/usuarios/${id}`),

  crearUsuario: (data: UsuarioCreate) =>
    api.post<UsuarioAdmin>('/usuarios', data),

  actualizarUsuario: (id: string, data: UsuarioUpdate) =>
    api.patch<UsuarioAdmin>(`/usuarios/${id}`, data),

  eliminarUsuario: (id: string) =>
    api.delete(`/usuarios/${id}`),

  resetearPassword: (id: string) =>
    api.post<{ password_temporal: string }>(`/usuarios/${id}/reset-password`),

  // Roles
  listarRoles: () =>
    api.get<RolAdmin[]>('/roles'),

  obtenerRol: (id: string) =>
    api.get<RolAdmin>(`/roles/${id}`),

  crearRol: (data: RolCreate) =>
    api.post<RolAdmin>('/roles', data),

  actualizarRol: (id: string, data: Partial<RolCreate>) =>
    api.patch<RolAdmin>(`/roles/${id}`, data),

  eliminarRol: (id: string) =>
    api.delete(`/roles/${id}`),

  // Permisos disponibles
  listarPermisos: () =>
    api.get<string[]>('/roles/permisos'),

  // Tipos de Ticket
  listarTiposTicket: () =>
    api.get<TipoTicketConfig[]>('/config/tipos-ticket'),

  actualizarTipoTicket: (id: string, data: Partial<TipoTicketConfig>) =>
    api.patch<TipoTicketConfig>(`/config/tipos-ticket/${id}`, data),

  // Tipos de Actividad
  listarTiposActividad: () =>
    api.get<TipoActividadConfig[]>('/config/tipos-actividad'),

  actualizarTipoActividad: (id: string, data: Partial<TipoActividadConfig>) =>
    api.patch<TipoActividadConfig>(`/config/tipos-actividad/${id}`, data),

  // Categorías de Conocimiento
  listarCategoriasConocimiento: () =>
    api.get<CategoriaConocimientoConfig[]>('/config/categorias-conocimiento'),

  actualizarCategoriaConocimiento: (id: string, data: Partial<CategoriaConocimientoConfig>) =>
    api.patch<CategoriaConocimientoConfig>(`/config/categorias-conocimiento/${id}`, data),

  // Módulos
  listarModulos: (soloActivos: boolean = false) =>
    api.get<ModuloConfig[]>(`/config/modulos?solo_activos=${soloActivos}`),

  crearModulo: (data: ModuloCreate) =>
    api.post<ModuloConfig>('/config/modulos', data),

  actualizarModulo: (id: string, data: ModuloUpdate) =>
    api.patch<ModuloConfig>(`/config/modulos/${id}`, data),

  desactivarModulo: (id: string) =>
    api.delete(`/config/modulos/${id}`),
};
